import sumBy from "lodash/sumBy";

import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { isUuidV4 } from "fond/utils";

export type CountsByCategory = { points: number; lines: number; polygons: number; comments: number; other: number };

export type LayerTotals = { count: number | null; length: number | null };

export type FeatureTotalsEntry = LayerTotals & { preference: "count" | "length" | null };

export type FeatureTotals = {
  [key: string]: FeatureTotalsEntry;
};

export type FeatureTotalsSummary = {
  count: number;
  length: number;
  countsByCategory: CountsByCategory;
};

const GEOMETRY_CATEGORIES: Record<string, keyof CountsByCategory> = {
  Point: "points",
  MultiPoint: "points",
  LineString: "lines",
  MultiLineString: "lines",
  Polygon: "polygons",
  MultiPolygon: "polygons",
};

const CATEGORY_LABELS: Record<keyof CountsByCategory, { singular: string; plural: string }> = {
  points: { singular: "point", plural: "points" },
  lines: { singular: "line", plural: "lines" },
  polygons: { singular: "polygon", plural: "polygons" },
  comments: { singular: "comment", plural: "comments" },
  other: { singular: "other", plural: "other" },
};

/**
 * Whether the backend can supply a per-sublayer breakdown for a layer.
 */
export const canFetchSublayerTotals = (layer: LayerConfig): boolean => isUuidV4(layer.ID) && layer.Children.length > 0;

/**
 * How much of a layer is visible on the map.
 *
 * - `partial` indicates that only some of the layer's sublayers are visible.
 * - `visible` indicates that the layer is fully visible.
 * - `hidden` indicates that the layer is not visible at all.
 */
export type LayerKeyVisibility = { status: "visible" | "hidden" } | { status: "partial"; visibleSublayerIdSets: string[][] };

const VISIBLE: LayerKeyVisibility = { status: "visible" };
const HIDDEN: LayerKeyVisibility = { status: "hidden" };

interface ResolveLayerKeyVisibilityOptions {
  /** Whether the entity (and every one of its ancestors) is toggled on in the legend. */
  isEntityVisible: (id: string) => boolean;

  /** Whether the map draws the layer at all. */
  isRendered: (id: string) => boolean;

  /** Whether we can calculate a per-sublayer count for the layer's sublayers. */
  canBreakDown: (layer: LayerConfig) => boolean;
}

const resolveLayerVisibility = (layer: LayerConfig, options: ResolveLayerKeyVisibilityOptions): LayerKeyVisibility => {
  const { isEntityVisible, isRendered, canBreakDown } = options;

  if (!isRendered(layer.ID) || !isEntityVisible(layer.ID)) return HIDDEN;

  if (layer.Styles.length > 0 || layer.Children.length === 0) return VISIBLE;

  const visibleSublayerIds = layer.Children.filter(isEntityVisible);
  if (visibleSublayerIds.length === layer.Children.length) return VISIBLE;
  if (visibleSublayerIds.length === 0) return HIDDEN;

  // if the count can't be broken down into sublayers then we can only count the entire layer
  return canBreakDown(layer) ? { status: "partial", visibleSublayerIdSets: [visibleSublayerIds] } : VISIBLE;
};

const combineLayerVisibility = (current: LayerKeyVisibility | undefined, next: LayerKeyVisibility): LayerKeyVisibility => {
  if (!current) return next;
  if (current.status === "visible" || next.status === "visible") return VISIBLE;
  if (current.status === "partial" && next.status === "partial") {
    return { status: "partial", visibleSublayerIdSets: [...current.visibleSublayerIdSets, ...next.visibleSublayerIdSets] };
  }
  return current.status === "partial" ? current : next;
};

/**
 * Resolve the visibility of each layer key.
 *
 * Multiple layer configurations can refer to the same layer key (e.g. in planner projects, each of the
 * cost-to-serve layers displays "output/service_location"). The visibility of a layer key is obtained by
 * merging the visibility of each layer configuration that refers to the same layer key.
 */
export const resolveLayerKeyVisibility = (
  layerConfigs: Array<LayerConfig | SublayerConfig>,
  options: ResolveLayerKeyVisibilityOptions
): Record<string, LayerKeyVisibility> => {
  const result: Record<string, LayerKeyVisibility> = {};

  layerConfigs.forEach((layerConfig) => {
    if (layerConfig.Type !== "LAYER" || !layerConfig.Key) return;
    result[layerConfig.Key] = combineLayerVisibility(result[layerConfig.Key], resolveLayerVisibility(layerConfig, options));
  });

  return result;
};

const sumTotals = (totals: Array<LayerTotals | undefined>): LayerTotals => ({
  count: sumBy(totals, (total) => total?.count ?? 0),
  length: sumBy(totals, (total) => total?.length ?? 0),
});

const largestTotals = (totals: LayerTotals[]): LayerTotals => ({
  count: Math.max(0, ...totals.map((total) => total.count ?? 0)),
  length: Math.max(0, ...totals.map((total) => total.length ?? 0)),
});

interface AggregateFeatureTotalsOptions {
  geometryTypeByLayerKey: Record<string, string | null>;
  visibilityByLayerKey?: Record<string, LayerKeyVisibility>;
  /**
   * Sublayer totals keyed by sublayer configuration ID.
   */
  sublayerTotals?: Record<string, LayerTotals>;
  /**
   * Number of comments within the same area.
   */
  commentCount?: number;
}

/**
 * Aggregate backend feature-totals into a summary of lengths and counts by category.
 *
 * Only the visible portion of each layer is counted. A layer key missing from `visibilityByLayerKey` is
 * treated as visible.
 */
export const aggregateFeatureTotals = (
  layerFeatureTotals: Record<string, LayerTotals>,
  { geometryTypeByLayerKey, visibilityByLayerKey = {}, sublayerTotals = {}, commentCount = 0 }: AggregateFeatureTotalsOptions
): FeatureTotalsSummary => {
  const summary: FeatureTotalsSummary = {
    count: commentCount,
    length: 0,
    countsByCategory: { points: 0, lines: 0, polygons: 0, comments: commentCount, other: 0 },
  };

  Object.entries(layerFeatureTotals)
    // The API returns each layer's total under both its configuration ID (a UUID or None when the layer
    // lacks a configuration) and its layer key. We only consider the layer-key entries here to avoid
    // double-counting.
    .filter(([key]) => !isUuidV4(key) && key !== "None")
    .forEach(([key, layerTotals]) => {
      const visibility = visibilityByLayerKey[key] ?? VISIBLE;
      if (visibility.status === "hidden") return;

      const { count, length } =
        visibility.status === "partial"
          ? largestTotals(visibility.visibleSublayerIdSets.map((ids) => sumTotals(ids.map((id) => sublayerTotals[id]))))
          : layerTotals;

      const category = GEOMETRY_CATEGORIES[geometryTypeByLayerKey[key] ?? ""] ?? "other";
      summary.count += count ?? 0;
      summary.length += length ?? 0;
      summary.countsByCategory[category] += count ?? 0;
    });

  return summary;
};

export type CategoryCount = {
  category: keyof CountsByCategory;
  count: number;
  /** Display label for the count, e.g. "2 points". */
  label: string;
};

/**
 * Lists the non-zero per-category counts in display order, labelled e.g. "2 points".
 */
export const listCategoryCounts = (countsByCategory: CountsByCategory): CategoryCount[] =>
  (Object.entries(CATEGORY_LABELS) as Array<[keyof CountsByCategory, { singular: string; plural: string }]>)
    .map(([category, { singular, plural }]) => {
      const count = countsByCategory[category];
      return { category, count, label: `${count.toLocaleString()} ${count === 1 ? singular : plural}` };
    })
    .filter(({ count }) => count > 0);
